<script lang="ts" setup>
import { ElNotification } from 'element-plus'
import {
  TERMINALS_TO_ALLOW_AUTOMATIC_PLATE_SETTING,
  TERMINALS_WHERE_LICENSE_PLATE_IS_OPTIONAL_USED_WHEN_BOOKING,
  TERMINALS_WITH_OWN_CHASSIS_OPTION_FOR_IMPORT_APPT,
  getTerminalLabel,
} from '~/constants'
import type { OrgConfig } from '~/models/organization'
import type { TerminalName } from '~/services/apiClient'
import { OrganizationsApi } from '~/services/apiClient'
import { useUserStore } from '~/stores/user'
import EmailAddressesInput from '~/components/inputs/EmailAddressesInput.vue'

const userStore = useUserStore()
const savedOrgPreferences = ref('')
const orgPreferences = ref(null as OrgConfig | null)

function markOrgPreferencesAsSaved(orgInfo: OrgConfig) {
  orgPreferences.value = orgInfo
  savedOrgPreferences.value = JSON.stringify(orgPreferences.value.toAPIFormat())
}

onMounted(() => {
  userStore.loadInfoFromAPI().then(async (userInfo) => {
    markOrgPreferencesAsSaved(userInfo.current_organization.config)
  })
})

const orgPreferencesModified = computed(() => {
  return (
    JSON.stringify(orgPreferences.value?.toAPIFormat()) !==
    savedOrgPreferences.value
  )
})

function saveOrganizationPreferences() {
  const api = new OrganizationsApi()
  if (!orgPreferences.value) throw new Error('This should not happen')
  api
    .updateOrganizationPreferencesOrganizationsPreferencesPut(
      orgPreferences.value.toAPIFormat()
    )
    .then(() => {
      ElNotification({
        title: 'Preferences saved',
        type: 'success',
      })
      userStore.loadInfoFromAPI().then((userInfo) => {
        markOrgPreferencesAsSaved(userInfo.current_organization.config)
      })
    })
    .catch(() => {
      ElNotification({
        message: 'Error saving preferences',
        type: 'error',
      })
    })
}

function toggleTerminalLicensePlatePrefill(terminal: TerminalName) {
  orgPreferences.value?.toggleShouldPrefillLicensePlate(terminal)
}

function toggleTerminalSetPlateIfNotSet(terminal: TerminalName) {
  orgPreferences.value?.toggleShouldSetPlateIfNotSet(terminal)
}

const chassisPreference = computed({
  get: () => {
    return orgPreferences.value?.defaultChassisType === 'own'
  },
  set: (value: boolean) => {
    if (orgPreferences.value) {
      orgPreferences.value.defaultChassisType = value ? 'own' : 'pool'
    }
  },
})

const supportedChassisTerminals = computed(() => {
  const terminals = Array.from(
    TERMINALS_WITH_OWN_CHASSIS_OPTION_FOR_IMPORT_APPT
  ).map((terminal) => getTerminalLabel(terminal))
  if (terminals.length <= 1) return terminals.join('')
  return terminals.slice(0, -1).join(', ') + ' and ' + terminals.slice(-1)
})
</script>

<template>
  <div v-loading="userStore.loading" class="min-h-30">
    <el-form v-if="orgPreferences" label-position="right">
      <!-- Email Notification Section with Save Button -->
      <div class="flex justify-between items-center mb-3">
        <h3 class="subsection-header !mt-0 !mb-0">Auto Book Email Notifications</h3>
        <el-button
          type="success"
          :disabled="!orgPreferencesModified"
          @click="saveOrganizationPreferences"
        >
          Save
        </el-button>
      </div>
      
      <el-form-item>
        <template #label>
          Email Addresses to send Auto Book Notifications to:
          <Help title="Auto Book Email Notifications" class="help inline-block">
            <p>
              All Auto Book notifications will be sent to these email addresses,
              in addition to the user who set up Auto Book and assigned users.
            </p>
            <p>
              This allows you to ensure specific administrators or managers
              always receive notifications without needing to assign them to
              containers.
            </p>
          </Help>
        </template>
      </el-form-item>
      <el-form-item>
        <EmailAddressesInput
          v-model="orgPreferences.autoBookNotificationEmails"
          placeholder="admin@company.com, operations@company.com..."
          help-text="Enter email addresses separated by commas, new lines, or spaces. All Auto Book notifications will be sent to these addresses."
          :min-rows="3"
        />
      </el-form-item>

      <!-- Default Chassis Type Section -->
      <h3 class="subsection-header">Default Chassis Type</h3>
      <el-form-item>
        <template #label>
          Control what chassis type is used by default for appointments.
          <Help title="Default Chassis Type" class="help inline-block">
            <p>
              This setting determines the default chassis type for appointment
              bookings across terminals that support chassis selection:
              <strong
                >{{ supportedChassisTerminals }} import appointments</strong
              >.
            </p>
            <p>
              <strong>Own Chassis:</strong> Use your company's own chassis for
              appointments.
            </p>
            <p>
              <strong>Pool Chassis:</strong> Use terminal-provided pool chassis
              for appointments.
            </p>
            <p>
              This default applies to manual booking and auto-booking flows.
              Users can still manually override this default for individual
              appointments if needed.
            </p>
          </Help>
        </template>
      </el-form-item>
      <el-form-item>
        <div class="flex items-center gap-3">
          <el-switch
            v-model="chassisPreference"
            inline-prompt
            active-color="#13ce66"
            inactive-color="#ff4949"
            active-text="Own"
            inactive-text="Pool"
            :active-value="true"
            :inactive-value="false"
          />
          <span class="text-sm text-gray-600">
            Current default:
            <strong>{{
              chassisPreference ? 'Own Chassis' : 'Pool Chassis'
            }}</strong>
          </span>
        </div>
      </el-form-item>

      <!-- Terminal License Plate Settings -->
      <h3 class="subsection-header">
        Terminal License Plate Pre-fill Settings
      </h3>

      <el-form-item>
        <template #label>
          Automatically Set License Plate if Not Set (2 hours before)
          <Help title="License plate pre-filling" class="help inline-block">
            <p>
              This is an alternative to setting a "dummy" license plate when
              bookings an appointment. At some terminals, like APM, if the dummy
              plate is not updated and conflicts with another appointment,
              issues can arise at the gate. This system is a fallback to allow
              not setting a plate when booking, knowing that if dispatch does
              not update the plate in time, Dray Dog will set a dummy plate two
              hours before the appointment to avoid cancellation.
            </p>
            <p>
              In general across all terminals, we <em>recommend</em> this system
              because it increases that chance your truck will auto in-gate.
              This is for two reasons:
            </p>
            <ul>
              <li>Dispatchers are more likely to set the correct plate</li>
              <li>
                Dray Dog will only set a plate to avoid cancellation, reducing
                the chances that multiple appointments will have the same plate.
              </li>
            </ul>
          </Help>
        </template>
      </el-form-item>
      <el-form-item
        v-for="terminal of TERMINALS_TO_ALLOW_AUTOMATIC_PLATE_SETTING"
        :key="terminal"
      >
        <template #label>
          <span class="min-w-16 ml-8">
            {{ getTerminalLabel(terminal) }}
          </span>
        </template>
        <el-switch
          :model-value="orgPreferences.shouldSetPlateIfNotSet(terminal)"
          @click="toggleTerminalSetPlateIfNotSet(terminal)"
        />
      </el-form-item>

      <el-form-item>
        <template #label>
          Pre-fill license plates at:
          <Help title="License plate pre-filling" class="help inline-block">
            <p>
              APM will cancel appointments without a license plate set a couple
              hours before the appointment time starts. If you want to make
              absolutely sure your appointments don't get cancelled, you can set
              a "dummy" license plate. This option sets a dummy license plate by
              default, although users can still "clear" it.
            </p>
            <p>
              In general across all terminals, ee do not
              <em>recommend</em> using dummy license plates because it reduces
              that chance your truck will auto in-gate. This is for two reasons:
            </p>
            <ul>
              <li>Dispatchers are more likely to set the correct plate</li>
              <li>
                At APM, the gate gets confused if there are multiple scheduled
                appointments that have the same plate
              </li>
            </ul>
          </Help>
        </template>
      </el-form-item>
      <el-form-item
        v-for="terminal of TERMINALS_WHERE_LICENSE_PLATE_IS_OPTIONAL_USED_WHEN_BOOKING"
        :key="terminal"
      >
        <template #label>
          <span class="min-w-16 ml-8">
            {{ getTerminalLabel(terminal) }}
          </span>
        </template>
        <el-switch
          :model-value="orgPreferences.shouldPrefillLicensePlate(terminal)"
          @click="toggleTerminalLicensePlatePrefill(terminal)"
        />
      </el-form-item>
    </el-form>
  </div>
</template>

<style scoped lang="scss">
ol > li {
  @apply list-decimal;
}
</style>
